import React from 'react';
import { useAuth } from '../../api/auth/AuthContext';
import { useSkuEntitlements, useSkuEntitlementsLoaded, can, SkuEntitlements } from './PriceListEntitlements';

// ── Route definition ──────────────────────────────────────────────────────────
//
// Add new admin routes here. Each entry controls:
//   - visibility (computed from entitlements / roles)
//   - adminOnly: true → shows a lock indicator in sidebar + home card
//
// Admin.tsx reads this via buildAdminRoutes() (uses local state — it IS the provider).
// AdminHome.tsx reads this via useAdminRoutes() hook (consumes context).

export interface AdminRoute {
  path: string;
  label: string;
  description: string;
  Icon: React.FC<{ size?: number }>;
  visible: boolean;
  adminOnly: boolean;
}

// ── Icons (accept size prop so sidebar and home card share the same component) ─

export const IconPriceList: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

export const IconPermissions: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

export const IconDiagnostico: React.FC<{ size?: number }> = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);

// ── Route builder ─────────────────────────────────────────────────────────────
//
// Called directly by Admin.tsx (which owns the context state, not consumes it).
// Called indirectly via useAdminRoutes() by child pages.

export function buildAdminRoutes(params: {
  entitlements: SkuEntitlements;
  entitlementsLoaded: boolean;
  isAdmin: boolean;
}): AdminRoute[] {
  const { entitlements, entitlementsLoaded, isAdmin } = params;

  return [
    {
      path: '/admin/lista-de-precios',
      label: 'Lista de Precios',
      description: 'Búsqueda de productos y precios.',
      Icon: IconPriceList,
      visible: entitlementsLoaded && can.anyPriceListTab(entitlements),
      adminOnly: false,
    },
    {
      path: '/admin/permisos',
      label: 'Permisos',
      description: 'Gestión de permisos de acceso y directorio de usuarios del portal.',
      Icon: IconPermissions,
      visible: isAdmin,
      adminOnly: true,
    },
    {
      path: '/admin/diagnostico',
      label: 'Diagnóstico',
      description: 'Estado del backend, publicación de catálogos y configuración del sitio.',
      Icon: IconDiagnostico,
      visible: isAdmin,
      adminOnly: true,
    },
  ];
}

// ── Hook — use inside components rendered under the Admin layout ───────────────

export function useAdminRoutes(): AdminRoute[] {
  const { roles } = useAuth();
  const entitlements = useSkuEntitlements();
  const loaded = useSkuEntitlementsLoaded();
  return buildAdminRoutes({
    entitlements,
    entitlementsLoaded: loaded,
    isAdmin: roles.includes('Admin'),
  });
}
